import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  IconAlertTriangle,
  IconArrowsMaximize,
  IconCheck,
  IconClock,
  IconCloudCheck,
  IconDownload,
  IconDots,
  IconGripVertical,
  IconHome,
  IconLoader2,
  IconPencil,
  IconPhoto,
  IconPlayerPlay,
  IconPlayerStop,
  IconRefresh,
  IconSparkles,
  IconTrash,
} from "@tabler/icons-react";
import { ApiError, userApi } from "./api";
import { clientId } from "./client-id";
import { exportOrDownloadPptx } from "./pptx-export";
import type { OutputSlide, ProjectDetailResponse, ProjectImageModeResultResponse, ProjectRender } from "./project-types";
import { Badge, Button } from "./ui";
import { notifyCreditActionSubmitted, requestCreditSpend } from "./credit-interactions";
import { ImageModeChat } from "./existing-ppt-beautify-chat";
import { AdvancedBeautifyAnnotation } from "./advanced-beautify-annotation";
import { MAX_ADVANCED_BEAUTIFY_REGIONS } from "./advanced-beautify-annotation-utils";
import { reorderAdvancedBeautifySlides } from "./advanced-beautify";
import { useImage2RevisionSession } from "./image2-revision-session";
import { userFacingGenerationMessage } from "./user-facing-generation-message";

type ImageModePhase = "planning" | "awaiting_credits" | "queued" | "rendering" | "complete" | "failed" | "cancelled";
type ImageModeSlideStatus = "waiting" | "running" | "succeeded" | "failed" | "cancelled";
export type ImageModeCenterPhase = "parsing" | "outline" | "images";

interface ImageModeView {
  phase: ImageModePhase;
  percent: number;
  totalSlides: number;
  completedSlides: number;
  currentSlide: number;
  headline: string;
  subline: string;
  detailText: string;
}

interface ImageModeSlide {
  sourceSlideId?: string;
  index: number;
  title: string;
  status: ImageModeSlideStatus;
  imageUrl?: string;
  thumbnailUrl?: string;
  sourcePreviewUrl?: string;
  progressText?: string;
  errorMessage?: string;
}

const EXCLUDED_IMAGE2_PRODUCT_CODES = new Set(["advanced-beautify-image2", "image2-slide-revision-preview"]);

export type SingleFlightState<T> = { key?: string; promise?: Promise<T> };

export function runSingleFlight<T>(state: SingleFlightState<T>, key: string, operation: () => Promise<T>): Promise<T> {
  if (state.key === key && state.promise) return state.promise;
  const promise = operation().finally(() => {
    if (state.promise === promise) {
      state.key = undefined;
      state.promise = undefined;
    }
  });
  state.key = key;
  state.promise = promise;
  return promise;
}

export function ImageModeGeneratingPage() {
  const { taskId = "" } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<ProjectDetailResponse | undefined>(() => loadSessionDetail(taskId));
  const [error, setError] = useState("");
  const [requestId, setRequestId] = useState("");
  const [busy, setBusy] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [autoFollow, setAutoFollow] = useState(true);
  const [firstImageRenderId, setFirstImageRenderId] = useState("");
  const [supersededRenderId, setSupersededRenderId] = useState("");
  const [slideOrderOverride, setSlideOrderOverride] = useState<ImageModeSlide[]>();
  const [draggedSlideIndex, setDraggedSlideIndex] = useState<number>();
  const [dragOverSlideIndex, setDragOverSlideIndex] = useState<number>();
  const [initialLoadResolved, setInitialLoadResolved] = useState(false);
  const [recognitionAnimatedSlide, setRecognitionAnimatedSlide] = useState(0);
  const stageRef = useRef<HTMLElement>(null);
  const detailRef = useRef(detail);
  const taskIdRef = useRef(taskId);
  const loadFlightRef = useRef<SingleFlightState<ProjectDetailResponse>>({});

  const load = useCallback(async (silent = false) => {
    try {
      const cached = detailRef.current;
      const cachedRender = pickImageModeRender(cached);
      const preferLightResult = !cached || deriveImageModeView(cached, cachedRender).phase === "complete";
      const requestKey = `${taskId}:${preferLightResult ? "result" : "detail"}`;
      const data = await runSingleFlight(loadFlightRef.current, requestKey, async () => {
        const requestOptions = { cache: "no-store" as const, headers: { "cache-control": "no-cache" } };
        if (preferLightResult) {
          const result = await userApi<ProjectImageModeResultResponse>(
            `/api/app/v2/ppt-tasks/${encodeURIComponent(taskId)}/image-mode-result`,
            requestOptions,
          );
          if (result.completed) return imageModeResultToDetail(result);
        }
        return userApi<ProjectDetailResponse>(`/api/app/v2/ppt-tasks/${encodeURIComponent(taskId)}/generation-view?mode=image`, requestOptions);
      });
      if (taskIdRef.current !== taskId) return;
      detailRef.current = data;
      setDetail(data);
      setInitialLoadResolved(true);
      setSlideOrderOverride(undefined);
      sessionStorage.setItem(`pptdeep:task:${taskId}`, JSON.stringify(data));
      setRequestId("");
      if (!silent) setError("");
    } catch (caught) {
      if (!silent) {
        setError(caught instanceof Error ? caught.message : "图片模式项目加载失败");
        setRequestId(caught instanceof ApiError ? caught.requestId || "" : "");
      }
    }
  }, [taskId]);

  useEffect(() => {
    const cached = loadSessionDetail(taskId);
    const taskChanged = taskIdRef.current !== taskId;
    taskIdRef.current = taskId;
    detailRef.current = cached;
    if (taskChanged) loadFlightRef.current = {};
    setDetail(cached);
    setCurrentSlide(0);
    setAutoFollow(true);
    setFirstImageRenderId("");
    setSupersededRenderId("");
    setSlideOrderOverride(undefined);
    setDraggedSlideIndex(undefined);
    setDragOverSlideIndex(undefined);
    setError("");
    setRequestId("");
    setMenuOpen(false);
    setDeleteOpen(false);
    setInitialLoadResolved(false);
  }, [taskId]);
  useEffect(() => { void load(Boolean(loadSessionDetail(taskId))); }, [load, taskId]);

  const imageRender = useMemo(() => pickImageModeRender(detail), [detail]);
  const isExistingPptBeautify = detail?.task.workflowMode === "existing_ppt_beautify";
  const view = useMemo(() => deriveImageModeView(detail, imageRender), [detail, imageRender]);
  const generatedSlides = useMemo(() => buildImageModeSlides(detail, imageRender), [detail, imageRender]);
  const slides = slideOrderOverride || generatedSlides;
  const shouldPoll = shouldPollImageMode(detail, imageRender, initialLoadResolved);
  const activeSlide = slides[currentSlide] || slides[0];
  const latestGeneratedSlide = useMemo(
    () => [...slides].reverse().find((slide) => Boolean(slide.imageUrl)),
    [slides],
  );
  const currentRenderIsSuperseded = Boolean(imageRender?.id && imageRender.id === supersededRenderId);
  const hasGeneratedImage = !currentRenderIsSuperseded && Boolean(latestGeneratedSlide);
  const firstImageArrived = !currentRenderIsSuperseded && Boolean(imageRender?.id && firstImageRenderId === imageRender.id);
  const centerPhase = currentRenderIsSuperseded
    ? detail?.planningPreview ? "outline" : "parsing"
    : deriveImageModeCenterPhase(detail, imageRender, firstImageArrived || hasGeneratedImage);
  const displayedSlide = isExistingPptBeautify && centerPhase === "parsing"
    ? slides[recognitionAnimatedSlide] || activeSlide
    : activeSlide;
  const selectedPage = activeSlide?.index || currentSlide + 1;
  const revision = useImage2RevisionSession({
    render: imageRender,
    selectedPage,
    enabled: view.phase === "complete" && Boolean(activeSlide?.imageUrl),
    idempotencyPrefix: isExistingPptBeautify ? "existing-ppt" : "image2",
    onRefresh: () => load(true),
    onSelectPage: (page) => {
      setCurrentSlide(Math.max(0, page - 1));
      setAutoFollow(false);
    },
  });

  useEffect(() => {
    if (!shouldPoll) return;
    const timer = window.setInterval(() => void load(true), 2500);
    return () => window.clearInterval(timer);
  }, [load, shouldPoll]);

  useEffect(() => {
    setCurrentSlide((previous) => Math.max(0, Math.min(previous, Math.max(0, slides.length - 1))));
  }, [slides.length]);

  useEffect(() => {
    if (!autoFollow) return;
    const targetIndex = existingPptAutoFollowSlideIndex(slides, view);
    if (targetIndex !== undefined) setCurrentSlide(targetIndex);
  }, [autoFollow, slides, view]);

  useEffect(() => {
    const active = isExistingPptBeautify && centerPhase === "parsing" && view.phase === "planning";
    if (!active) {
      setRecognitionAnimatedSlide(0);
      return;
    }
    const totalSlides = Math.max(1, view.totalSlides);
    let visibleIndex = 0;
    let timer: number | undefined;
    setRecognitionAnimatedSlide(0);
    setCurrentSlide(0);
    const scheduleNext = () => {
      if (visibleIndex >= totalSlides - 1) return;
      timer = window.setTimeout(() => {
        visibleIndex += 1;
        setRecognitionAnimatedSlide(visibleIndex);
        setCurrentSlide(visibleIndex);
        scheduleNext();
      }, existingPptRecognitionStepDelayMs(visibleIndex + 1, totalSlides));
    };
    scheduleNext();
    return () => {
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [centerPhase, isExistingPptBeautify, taskId, view.phase, view.totalSlides]);

  useEffect(() => {
    if (!isExistingPptBeautify || !["planning", "queued", "rendering"].includes(view.phase)) return;
    const active = document.querySelector<HTMLElement>(".advanced-beautify-rail button.active");
    active?.scrollIntoView({
      block: "nearest",
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
    });
  }, [currentSlide, isExistingPptBeautify, view.phase]);

  useEffect(() => {
    if (hasGeneratedImage && imageRender?.id) setFirstImageRenderId(imageRender.id);
  }, [hasGeneratedImage, imageRender?.id]);

  const cancel = async () => {
    if (busy || !["planning", "queued", "rendering"].includes(view.phase)) return;
    setBusy("cancel");
    setError("");
    try {
      await userApi(`/api/app/v2/ppt-tasks/${encodeURIComponent(taskId)}/cancel`, { method: "POST", body: "{}" });
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "取消图片模式生成失败");
      setRequestId(caught instanceof ApiError ? caught.requestId || "" : "");
    } finally {
      setBusy("");
    }
  };

  const retry = async () => {
    if (busy || !["failed", "cancelled"].includes(view.phase)) return;
    try {
      const generation = [...(detail?.generations || [])].reverse().find((item) =>
        !EXCLUDED_IMAGE2_PRODUCT_CODES.has(item.productCode));
      const approved = await requestCreditSpend({
        operation: "image2_generate",
        slideCount: detail?.deckVersion?.document.slides.length || detail?.task.requestedPageCount || view.totalSlides || 1,
        ...(generation && generation.creditCost > 0 ? { fixedPoints: generation.creditCost } : {}),
      });
      if (!approved) return;
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "积分信息加载失败，请稍后重试");
      return;
    }
    setBusy("retry");
    setError("");
    try {
      await userApi(`/api/app/v2/ppt-tasks/${encodeURIComponent(taskId)}/retry`, {
        method: "POST",
        body: JSON.stringify({ idempotencyKey: clientId() }),
      });
      notifyCreditActionSubmitted();
      const unfinished = slides.find((slide) => slide.status === "failed" || slide.status === "waiting");
      if (unfinished) setCurrentSlide(Math.max(0, unfinished.index - 1));
      setAutoFollow(true);
      setSupersededRenderId(imageRender?.id || "");
      setFirstImageRenderId("");
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "继续生成图片模式失败");
      setRequestId(caught instanceof ApiError ? caught.requestId || "" : "");
    } finally {
      setBusy("");
    }
  };

  const resumeAfterPurchase = async () => {
    if (busy || view.phase !== "awaiting_credits") return;
    try {
      const approved = await requestCreditSpend({
        operation: "image2_generate",
        slideCount: view.totalSlides,
      });
      if (!approved) return;
      setBusy("resume-credits");
      setError("");
      await userApi(`/api/app/v2/ppt-tasks/${encodeURIComponent(taskId)}/existing-ppt-beautify/resume`, {
        method: "POST",
        body: "{}",
      });
      notifyCreditActionSubmitted();
      setAutoFollow(true);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "继续生成失败");
      setRequestId(caught instanceof ApiError ? caught.requestId || "" : "");
    } finally {
      setBusy("");
    }
  };

  const abandonAfterPlanning = async () => {
    if (busy || view.phase !== "awaiting_credits") return;
    if (!window.confirm("确认暂不生成？原始 PPT 将移入临时目录，并在 24 小时内自动清理。")) return;
    setBusy("abandon");
    setError("");
    try {
      await userApi(`/api/app/v2/ppt-tasks/${encodeURIComponent(taskId)}/existing-ppt-beautify/abandon`, {
        method: "POST",
        body: "{}",
      });
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "暂不生成操作失败");
    } finally {
      setBusy("");
    }
  };

  const exportPptx = async () => {
    if (!imageRender || busy) return;
    setBusy("export");
    setError("");
    try {
      await exportOrDownloadPptx(imageRender);
      await load(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "图片模式 PPT 下载失败");
      setRequestId(caught instanceof ApiError ? caught.requestId || "" : "");
    } finally {
      setBusy("");
    }
  };

  const deleteProject = async () => {
    if (busy || ["planning", "generating"].includes(detail?.task.status || "")) return;
    setBusy("delete");
    setError("");
    setRequestId("");
    try {
      await userApi(`/api/app/v2/ppt-tasks/${encodeURIComponent(taskId)}`, { method: "DELETE" });
      sessionStorage.removeItem(`pptdeep:task:${taskId}`);
      navigate("/app/projects", { replace: true });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "删除项目失败");
      setRequestId(caught instanceof ApiError ? caught.requestId || "" : "");
      setDeleteOpen(false);
    } finally {
      setBusy("");
    }
  };

  const reorderExistingPptSlide = async (fromIndex: number, toIndex: number) => {
    if (busy || revision.processing || view.phase !== "complete" || !imageRender
      || fromIndex === toIndex) return;
    const reordered = reorderAdvancedBeautifySlides(slides, fromIndex, toIndex);
    const slideOrder = reordered.map((slide) => slide.index);
    const selected = slides[currentSlide];
    const previousSelectedIndex = currentSlide;
    const nextSelectedIndex = Math.max(0, reordered.indexOf(selected));
    setCurrentSlide(nextSelectedIndex);
    setAutoFollow(false);
    setBusy("reorder");
    setError("");
    setRequestId("");
    setSlideOrderOverride(reordered.map((slide, index) => ({ ...slide, index: index + 1 })));
    try {
      await userApi(`/api/app/v2/ppt-tasks/${encodeURIComponent(taskId)}/existing-ppt-beautify/reorder`, {
        method: "POST",
        body: JSON.stringify({ renderId: imageRender.id, slideOrder }),
      });
      revision.resetAll();
      await Promise.all([load(), revision.reloadMessages()]);
    } catch (caught) {
      setSlideOrderOverride(undefined);
      setCurrentSlide(previousSelectedIndex);
      setError(caught instanceof Error ? caught.message : "页面顺序保存失败");
      setRequestId(caught instanceof ApiError ? caught.requestId || "" : "");
    } finally {
      setBusy("");
      setDraggedSlideIndex(undefined);
      setDragOverSlideIndex(undefined);
    }
  };

  if (!detail && error) {
    return <section className="image-mode-load-state">
      <strong>暂时无法打开图片模式生成网页</strong>
      <p>{error}</p>
      {requestId && <code>Request ID: {requestId}</code>}
      <Button variant="secondary" onClick={() => void load()}>重新加载</Button>
    </section>;
  }

  if (!detail) return <div className="image-mode-loading"><IconLoader2 size={24}/><strong>正在读取图片模式项目...</strong></div>;

  if (isExistingPptBeautify) {
    const active = ["planning", "queued", "rendering"].includes(view.phase);
    const recognizing = view.phase === "planning";
    const reorderReady = view.phase === "complete" && !busy && !revision.processing;
    const existingPptCanEdit = revision.canEdit && busy !== "reorder";
    const selectedSlideNumber = displayedSlide?.index || activeSlide?.index || 1;
    const previousOpenableIndex = findOpenableSlideIndex(slides, currentSlide, -1);
    const nextOpenableIndex = findOpenableSlideIndex(slides, currentSlide, 1);
    const selectExistingPptSlide = (index: number) => {
      const bounded = Math.max(0, Math.min(index, Math.max(0, slides.length - 1)));
      if (!existingPptSlideCanOpen(slides[bounded])) return;
      setCurrentSlide(bounded);
      setAutoFollow(slides[bounded]?.status === "running");
    };

    return <div className={`advanced-beautify-page existing-ppt-beautify-result-page phase-${view.phase}`} aria-busy={active || busy === "reorder"}>
      <header className="standard-generate-topbar">
        <div className="standard-generate-titlebar">
          <button type="button" onClick={() => navigate("/app")} aria-label="返回工作台"><IconHome size={19}/></button>
          <h1>{detail.task.title}</h1>
          <span>{detail.task.updatedAt ? `已保存 ${formatImageModeHeaderTime(detail.task.updatedAt)}` : phaseLabel(view.phase)}</span>
          <IconCloudCheck size={20}/>
        </div>
        <span aria-hidden="true"/>
        <div className="standard-generate-actions">
          {active && <button type="button" onClick={() => void load()} disabled={Boolean(busy)}><IconRefresh size={18}/>刷新</button>}
          {active && <button type="button" onClick={() => void cancel()} disabled={busy === "cancel"}><IconPlayerStop size={18}/>{busy === "cancel" ? "取消中" : "取消任务"}</button>}
          {view.phase === "awaiting_credits" && <button type="button" onClick={() => window.open("/app/account#membership", "_blank", "noopener")}><IconSparkles size={18}/>购买积分</button>}
          {view.phase === "awaiting_credits" && <button type="button" className="primary" onClick={() => void resumeAfterPurchase()} disabled={Boolean(busy)}><IconRefresh size={18}/>{busy === "resume-credits" ? "正在核对" : "我已购买，继续生成"}</button>}
          {view.phase === "awaiting_credits" && <button type="button" onClick={() => void abandonAfterPlanning()} disabled={Boolean(busy)}><IconTrash size={18}/>{busy === "abandon" ? "处理中" : "暂不生成"}</button>}
          {["failed", "cancelled"].includes(view.phase) && detail.task.errorCode !== "existing_ppt_abandoned" && <button type="button" onClick={() => void retry()} disabled={busy === "retry"}><IconRefresh size={18}/>{busy === "retry" ? "提交中" : "继续生成"}</button>}
          {!active && !["failed", "cancelled"].includes(view.phase) && <button type="button" disabled={!imageRender?.previewUrl || Boolean(busy)} onClick={() => imageRender?.previewUrl && window.open(imageRender.previewUrl, "_blank", "noopener")}><IconPlayerPlay size={18}/>预览播放</button>}
          <button type="button" className="primary" disabled={view.phase !== "complete" || !imageRender || Boolean(busy)} onClick={() => void exportPptx()}><IconDownload size={18}/>{busy === "export" ? "处理中" : "导出PPT"}</button>
          <ProjectMoreMenu
            open={menuOpen}
            disabled={Boolean(busy)}
            deleteDisabled={["planning", "generating"].includes(detail.task.status)}
            onToggle={() => setMenuOpen((value) => !value)}
            onDelete={() => { setMenuOpen(false); setDeleteOpen(true); }}
          />
        </div>
      </header>

      <div className="advanced-beautify-workspace">
        <aside className={`advanced-beautify-rail${busy === "reorder" ? " is-reordering" : ""}`} aria-label={view.phase === "complete" ? "美化页面列表，可拖动调整顺序" : "美化页面列表"}>
          {slides.map((slide, index) => <button
            type="button"
            key={slide.sourceSlideId || `${slide.index}:${slide.title}`}
            className={`${index === currentSlide ? "active" : ""}${!existingPptSlideCanOpen(slide) ? " is-locked" : ""}${draggedSlideIndex === index ? " is-dragging" : ""}${dragOverSlideIndex === index && draggedSlideIndex !== index ? ` is-drag-over ${draggedSlideIndex !== undefined && draggedSlideIndex < index ? "drop-after" : "drop-before"}` : ""}`}
            draggable={reorderReady && slide.status === "succeeded"}
            disabled={busy === "reorder" || recognizing || !existingPptSlideCanOpen(slide)}
            aria-label={`第 ${slide.index} 页：${slide.title}${slide.status === "running" ? "，正在生成，可查看生成进度" : !slide.imageUrl ? "，尚未生成，暂不可查看" : view.phase === "complete" ? "。拖动可调整顺序，或按 Alt 加上下方向键移动。" : "，已生成，可查看"}`}
            onClick={() => selectExistingPptSlide(index)}
            onKeyDown={(event) => {
              if (!reorderReady || !event.altKey || !["ArrowUp", "ArrowDown"].includes(event.key)) return;
              event.preventDefault();
              const target = event.key === "ArrowUp" ? index - 1 : index + 1;
              if (target >= 0 && target < slides.length) void reorderExistingPptSlide(index, target);
            }}
            onDragStart={(event) => {
              event.dataTransfer.effectAllowed = "move";
              event.dataTransfer.setData("text/plain", String(index));
              setDraggedSlideIndex(index);
              setDragOverSlideIndex(index);
            }}
            onDragOver={(event) => {
              if (draggedSlideIndex === undefined) return;
              event.preventDefault();
              event.dataTransfer.dropEffect = "move";
              setDragOverSlideIndex(index);
            }}
            onDrop={(event) => {
              event.preventDefault();
              const from = draggedSlideIndex ?? Number(event.dataTransfer.getData("text/plain"));
              if (Number.isInteger(from)) void reorderExistingPptSlide(from, index);
            }}
            onDragEnd={() => {
              setDraggedSlideIndex(undefined);
              setDragOverSlideIndex(undefined);
            }}
          >
            <IconGripVertical className="advanced-beautify-drag-handle" size={14}/>
            <span>{String(slide.index).padStart(2, "0")}</span>
            <div>{slide.thumbnailUrl
              ? <img draggable={false} src={slide.thumbnailUrl} alt={`第 ${slide.index} 页缩略图`}/>
              : <i>{slide.status === "running" ? `正在生成第 ${slide.index} 页` : slide.title}</i>}</div>
            <b className={slide.status}>{slideStateGlyph(slide.status)}</b>
          </button>)}
        </aside>

        <main className="advanced-beautify-stage-wrap">
          <section className="advanced-beautify-stage" ref={stageRef}>
            {centerPhase === "parsing"
              ? <ExistingPptRecognitionStage view={view} slide={displayedSlide}/>
              : centerPhase === "outline" && detail.planningPreview
                ? <ImageModeOutlinePreview detail={detail} view={view}/>
                : displayedSlide?.imageUrl
                  ? <AdvancedBeautifyAnnotation
                      imageSrc={displayedSlide.imageUrl}
                      alt={`${displayedSlide.title} 美化结果`}
                      regions={displayedSlide.index === selectedPage ? revision.regions : []}
                      drawing={displayedSlide.index === selectedPage
                        && revision.annotationMode
                        && revision.regions.length < MAX_ADVANCED_BEAUTIFY_REGIONS}
                      disabled={!existingPptCanEdit}
                      onAddRegion={revision.addRegion}
                      onRemoveRegion={revision.removeRegion}
                    />
                  : centerPhase === "images" && active
                    ? <ExistingPptImageGenerationStage view={view} slide={displayedSlide}/>
                  : imageRender?.previewUrl && view.phase === "complete"
                    ? <iframe src={imageRender.previewUrl} title="美化结果正式预览"/>
                    : <ImageModePlaceholder view={view} slide={displayedSlide}/>}
          </section>
          <div className="advanced-beautify-toolbar">
            <button type="button" disabled={previousOpenableIndex === undefined} onClick={() => previousOpenableIndex !== undefined && selectExistingPptSlide(previousOpenableIndex)}>上一页</button>
            <strong>{selectedSlideNumber} / {Math.max(1, slides.length)}</strong>
            <button type="button" disabled={nextOpenableIndex === undefined} onClick={() => nextOpenableIndex !== undefined && selectExistingPptSlide(nextOpenableIndex)}>下一页</button>
            <button
              type="button"
              className={`icon-only ${revision.annotationMode ? "active" : ""}`}
              disabled={!existingPptCanEdit}
              aria-label={revision.annotationMode ? "退出区域选择" : "开启区域选择"}
              aria-pressed={revision.annotationMode}
              aria-describedby="image-mode-annotation-help"
              onClick={() => revision.setAnnotationMode((value) => !value)}
            ><IconPencil size={16}/></button>
            <button type="button" className="icon-only" disabled={!revision.regions.length || revision.processing || busy === "reorder"} onClick={revision.clearRegions} aria-label="清空当前页画框"><IconTrash size={16}/></button>
            <button type="button" className="icon-only" aria-label="全屏预览" onClick={() => void stageRef.current?.requestFullscreen()}><IconArrowsMaximize size={18}/></button>
          </div>
          <p id="image-mode-annotation-help" className="sr-only" aria-live="polite">
            {revision.annotationMode
              ? `区域选择已开启，当前页已有 ${revision.regions.length} 个框。请在图片内拖动创建矩形。`
              : `当前页已有 ${revision.regions.length} 个定位框。`}
          </p>
        </main>

        <aside className="advanced-beautify-panel existing-ppt-chat-panel">
          <ImageModeChat
            variant="existing_ppt_beautify"
            detail={detail}
            render={imageRender}
            slides={slides}
            selectedIndex={currentSlide}
            phase={view.phase}
            percent={view.percent}
            externalError={requestId && error ? `${error}\nRequest ID: ${requestId}` : error}
            revisionSession={busy === "reorder" ? { ...revision, canEdit: false } : revision}
          />
        </aside>
      </div>
      <ProjectDeleteDialog
        open={deleteOpen}
        deleting={busy === "delete"}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={() => void deleteProject()}
      />
    </div>;
  }

  return <div
    className={`image-mode-page ${isExistingPptBeautify ? "existing-ppt-beautify-page" : ""} phase-${view.phase}`}
    aria-busy={["planning", "queued", "rendering"].includes(view.phase)}
  >
    <header className="image-mode-header">
      <div className="image-mode-titlebar">
        <button type="button" onClick={() => navigate("/app")} aria-label="返回工作台"><IconHome size={18}/></button>
        <div>
          <span>{isExistingPptBeautify ? "美化现有 PPT" : "图片模式生成网页"}</span>
          <h1>{detail.task.title}</h1>
          <p>{view.subline}</p>
        </div>
      </div>
      <div className="image-mode-actions">
        <Badge tone={badgeTone(view.phase)}>{phaseLabel(view.phase)}</Badge>
        <Button variant="secondary" onClick={() => void load()} disabled={Boolean(busy)}><IconRefresh size={16}/>刷新</Button>
        {["planning", "queued", "rendering"].includes(view.phase)
          ? <Button variant="secondary" onClick={() => void cancel()} disabled={busy === "cancel"}><IconPlayerStop size={16}/>{busy === "cancel" ? "取消中" : "取消任务"}</Button>
          : null}
        {["failed", "cancelled"].includes(view.phase)
          ? <Button onClick={() => void retry()} disabled={busy === "retry"}><IconRefresh size={16}/>{busy === "retry" ? "提交中" : "继续生成"}</Button>
          : <Button onClick={() => void exportPptx()} disabled={view.phase !== "complete" || !imageRender || busy === "export"}><IconDownload size={16}/>{busy === "export" ? "处理中" : "下载PPT"}</Button>}
        <ProjectMoreMenu
          open={menuOpen}
          disabled={Boolean(busy)}
          deleteDisabled={["planning", "generating"].includes(detail.task.status)}
          onToggle={() => setMenuOpen((value) => !value)}
          onDelete={() => { setMenuOpen(false); setDeleteOpen(true); }}
        />
      </div>
    </header>

    <div className="image-mode-workspace">
      <nav className="image-mode-rail" aria-label="图片模式页面列表">
        <div className="image-mode-rail-head"><strong>页面</strong><span>{Math.min(currentSlide + 1, slides.length || 1)} / {view.totalSlides}</span></div>
        <div className="image-mode-slide-list">
          {slides.map((slide) => <button
            type="button"
            key={slide.index}
            className={`${slide.status} ${slide.index - 1 === currentSlide ? "active" : ""}`}
            onClick={() => { setCurrentSlide(slide.index - 1); setAutoFollow(false); }}
          >
            <b>{String(slide.index).padStart(2, "0")}</b>
            <span>{slide.thumbnailUrl ? <img src={slide.thumbnailUrl} alt={`第 ${slide.index} 页缩略图`}/> : <IconPhoto size={18}/>}</span>
            <small>{slide.title}</small>
            <SlideStateIcon status={slide.status}/>
          </button>)}
        </div>
      </nav>

      <main className="image-mode-stage-panel">
        <div className="image-mode-stage-toolbar">
          <span>{isExistingPptBeautify ? "现有 PPT 美化" : "图片模式"} · {view.completedSlides}/{view.totalSlides} 页</span>
          {!autoFollow && ["planning", "queued", "rendering"].includes(view.phase)
            ? <button type="button" onClick={() => setAutoFollow(true)}>跟随生成</button>
            : null}
          <button
            type="button"
            className={revision.annotationMode ? "active" : ""}
            disabled={!revision.canEdit}
            aria-pressed={revision.annotationMode}
            aria-describedby="image-mode-annotation-help"
            onClick={() => revision.setAnnotationMode((value) => !value)}
          ><IconPencil size={15}/>{revision.annotationMode ? "关闭框选" : "开启框选"}</button>
          <button
            type="button"
            disabled={!revision.regions.length || revision.processing}
            aria-label="清空当前页画框"
            onClick={revision.clearRegions}
          ><IconTrash size={15}/>清空框选</button>
          <button type="button" onClick={() => void stageRef.current?.requestFullscreen()}>全屏查看</button>
        </div>
        <section className="image-mode-stage" ref={stageRef}>
          {centerPhase === "parsing"
            ? <ImageModeParsingProgress view={view} existingPpt={isExistingPptBeautify}/>
            : centerPhase === "outline" && detail.planningPreview
              ? <ImageModeOutlinePreview detail={detail} view={view}/>
              : displayedSlide?.imageUrl
                ? <AdvancedBeautifyAnnotation
                    imageSrc={displayedSlide.imageUrl}
                    alt={`${displayedSlide.title} 图片模式结果`}
                    regions={displayedSlide.index === selectedPage ? revision.regions : []}
                    drawing={displayedSlide.index === selectedPage
                      && revision.annotationMode
                      && revision.regions.length < MAX_ADVANCED_BEAUTIFY_REGIONS}
                    disabled={!revision.canEdit}
                    onAddRegion={revision.addRegion}
                    onRemoveRegion={revision.removeRegion}
                  />
                : imageRender?.previewUrl && view.phase === "complete"
                  ? <iframe src={imageRender.previewUrl} title="图片模式正式预览"/>
                  : <ImageModePlaceholder view={view} slide={displayedSlide}/>}
          {centerPhase === "images" && ["planning", "queued", "rendering"].includes(view.phase) && !displayedSlide?.imageUrl && <div className="image-mode-stage-overlay">
            <IconLoader2 size={26}/>
            <strong>{view.headline}</strong>
            <span>{view.detailText}</span>
          </div>}
        </section>
        <p id="image-mode-annotation-help" className="sr-only" aria-live="polite">
          {revision.annotationMode
            ? `框选模式已开启，当前页已有 ${revision.regions.length} 个框。请在图片内拖动创建矩形。`
            : `当前页已有 ${revision.regions.length} 个定位框。`}
        </p>
      </main>

      <aside className="image-mode-status-panel existing-ppt-chat-panel">
        <ImageModeChat
          variant={isExistingPptBeautify ? "existing_ppt_beautify" : "image_generation"}
          detail={detail}
          render={imageRender}
          slides={slides}
          selectedIndex={currentSlide}
          phase={view.phase}
          percent={view.percent}
          externalError={requestId && error ? `${error}\nRequest ID: ${requestId}` : error}
          revisionSession={revision}
        />
      </aside>
    </div>
    <ProjectDeleteDialog
      open={deleteOpen}
      deleting={busy === "delete"}
      onCancel={() => setDeleteOpen(false)}
      onConfirm={() => void deleteProject()}
    />
  </div>;
}

function ProjectMoreMenu({
  open,
  disabled,
  deleteDisabled,
  onToggle,
  onDelete,
}: {
  open: boolean;
  disabled: boolean;
  deleteDisabled: boolean;
  onToggle: () => void;
  onDelete: () => void;
}) {
  return <div className="standard-more-actions image-mode-more-actions">
    <button type="button" aria-label="更多操作" aria-haspopup="menu" aria-expanded={open} disabled={disabled} onClick={onToggle}><IconDots size={20}/></button>
    {open && <div className="standard-more-menu" role="menu">
      <button type="button" role="menuitem" className="danger" disabled={deleteDisabled} title={deleteDisabled ? "请先取消当前任务，再删除项目" : undefined} onClick={onDelete}><IconTrash size={17}/>删除项目</button>
    </div>}
  </div>;
}

function ProjectDeleteDialog({
  open,
  deleting,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  deleting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!open) return null;
  return <div className="standard-delete-overlay" role="dialog" aria-modal="true" aria-labelledby="image-mode-delete-title" aria-describedby="image-mode-delete-description">
    <section className="standard-delete-dialog">
      <span><IconTrash size={24}/></span>
      <h2 id="image-mode-delete-title">删除这个项目？</h2>
      <p id="image-mode-delete-description">项目会从项目列表移除，相关文件进入待清理区并保留 30 天，之后自动彻底清理。删除后页面内无法自行恢复。</p>
      <div><button type="button" autoFocus disabled={deleting} onClick={onCancel}>取消</button><button type="button" className="danger" disabled={deleting} onClick={onConfirm}>{deleting ? "删除中..." : "确认删除"}</button></div>
    </section>
  </div>;
}

function ImageModeParsingProgress({ view, existingPpt = false }: { view: ImageModeView; existingPpt?: boolean }) {
  const active = ["planning", "queued", "rendering"].includes(view.phase);
  return <div className={`image-mode-center-progress phase-${view.phase}`} role={active ? "status" : undefined} aria-live={active ? "polite" : undefined}>
    <div>{progressIcon(view.phase)}</div>
    <span>{existingPpt ? "现有 PPT 美化 · 原稿保护" : "图片模式 · 内容准备"}</span>
    <h2>{active && existingPpt ? "正在解析原始演示文稿" : active ? "正在解析资料内容" : view.headline}</h2>
    <p>{view.detailText}</p>
    <div className="image-mode-center-progress-track" role="progressbar" aria-label={`资料解析进度 ${view.percent}%`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={view.percent}><i style={{ width: `${view.percent}%` }}/></div>
    <dl className="image-mode-center-progress-stats">
      <div><dt>整体进度</dt><dd>{view.percent}%</dd></div>
      <div><dt>当前页面</dt><dd>{view.currentSlide}/{view.totalSlides}</dd></div>
      <div><dt>已完成</dt><dd>{view.completedSlides} 页</dd></div>
    </dl>
  </div>;
}

function ExistingPptRecognitionStage({
  view,
  slide,
}: {
  view: ImageModeView;
  slide?: ImageModeSlide;
}) {
  const current = slide?.index || view.currentSlide;
  const estimate = existingPptRecognitionEstimateLabel(view.totalSlides, current);
  if (view.phase === "awaiting_credits") {
    return <div className="existing-ppt-credit-gate" role="status">
      <span><IconCheck size={24}/></span>
      <h2>内容识别已经完成</h2>
      <p>{view.detailText}</p>
      <small>解析过程不消耗积分。购买后点击“我已购买，继续生成”，不会重新解析文件。</small>
    </div>;
  }
  return <div className="existing-ppt-recognition-stage" role="status" aria-live="polite">
    {slide?.thumbnailUrl
      ? <img src={slide.thumbnailUrl} alt="低清晰度原页预览"/>
      : <div className="existing-ppt-recognition-placeholder" aria-hidden="true"/>}
    <div className="existing-ppt-recognition-glass">
      <span className="existing-ppt-recognition-loader" aria-hidden="true">
        <i/><i/><IconSparkles size={30}/>
      </span>
      <strong key={current}>正在解析识别第 {current} 页</strong>
      <small>预计约 {estimate} 完成资料识别</small>
    </div>
  </div>;
}

export function existingPptRecognitionStepDelayMs(currentSlide: number, totalSlides: number): number {
  const current = Math.max(1, Math.floor(currentSlide));
  const total = Math.max(1, Math.floor(totalSlides));
  return 1_000 + ((current * 977 + total * 431) % 2_001);
}

export function existingPptRecognitionEstimateLabel(totalSlides: number, currentSlide = 1): string {
  const total = Math.max(1, Math.floor(totalSlides));
  const current = Math.max(1, Math.min(total, Math.floor(currentSlide)));
  const remaining = total - current + 1;
  const minimumSeconds = remaining + 1;
  const maximumSeconds = remaining * 3 + 3;
  if (maximumSeconds < 60) return `${minimumSeconds}–${maximumSeconds} 秒`;
  const minimumMinutes = Math.max(1, Math.floor(minimumSeconds / 60));
  const maximumMinutes = Math.max(minimumMinutes + 1, Math.ceil(maximumSeconds / 60));
  return `${minimumMinutes}–${maximumMinutes} 分钟`;
}

function ExistingPptImageGenerationStage({
  view,
  slide,
}: {
  view: ImageModeView;
  slide?: ImageModeSlide;
}) {
  const current = slide?.index || view.currentSlide;
  const running = slide?.status === "running" || view.phase === "rendering";
  return <div className="existing-ppt-image-generation-stage" role="status" aria-live="polite">
    {slide?.sourcePreviewUrl
      ? <img src={slide.sourcePreviewUrl} alt={`第 ${current} 页原始 PPT 低清预览`}/>
      : <div className="existing-ppt-image-generation-placeholder" aria-hidden="true"/>}
    <div className="existing-ppt-image-generation-content">
      <span className="existing-ppt-image-generation-loader" aria-hidden="true">
        <i/><i/><IconSparkles size={31}/>
      </span>
      <strong>{running ? `正在美化第 ${current} 页` : `正在准备第 ${current} 页`}</strong>
      <p>单页生成通常需要 2–5 分钟</p>
      <small>生成会在后台继续，左侧已完成页面可随时查看</small>
    </div>
  </div>;
}

function ImageModeOutlinePreview({ detail, view }: { detail: ProjectDetailResponse; view: ImageModeView }) {
  const snapshot = detail.planningPreview?.snapshot;
  const slides = snapshot?.slides || [];
  const active = ["planning", "queued", "rendering"].includes(view.phase);
  const existingPpt = detail.task.workflowMode === "existing_ppt_beautify";
  return <div className="image-mode-outline-preview" role={active ? "status" : undefined} aria-live={active ? "polite" : undefined}>
    <header>
      <div>
        <span>{existingPpt ? "原稿结构已锁定" : "内容大纲"} · {slides.length || view.totalSlides} 页</span>
        <h2>{snapshot?.title || detail.task.title || "PPT 内容规划"}</h2>
        <p>{active ? snapshot?.progressText || snapshot?.nextStep || view.detailText : view.detailText}</p>
      </div>
      <strong>{view.percent}%</strong>
    </header>
    <div className="image-mode-outline-progress" aria-label={`图片模式生成进度 ${view.percent}%`}><i style={{ width: `${view.percent}%` }}/></div>
    <section>
      {slides.map((slide) => <article key={`${slide.index}:${slide.title}`}>
        <b>{String(slide.index).padStart(2, "0")}</b>
        <div>
          <strong>{slide.title}</strong>
          <small>{slide.roleLabel || slide.role || "内容页"}</small>
          <p>{slide.message || slide.content?.[0] || "页面内容已经规划完成，正在等待首张图片。"}</p>
        </div>
      </article>)}
      {!slides.length && <div className="image-mode-outline-empty"><IconLoader2 size={25}/><span>大纲已生成，正在整理页面信息...</span></div>}
    </section>
    <footer className={active ? "active" : "terminal"}>
      {active ? <IconLoader2 size={15}/> : view.phase === "failed" ? <IconAlertTriangle size={15}/> : <IconClock size={15}/>}
      <span>{active
        ? existingPpt ? "正在逐页美化，首张预览返回前将持续显示原稿结构" : "正在生成首张图片，图片返回前将持续显示大纲"
        : view.headline}</span>
    </footer>
  </div>;
}

function ImageModePlaceholder({ view, slide }: { view: ImageModeView; slide?: ImageModeSlide }) {
  return <div className={`image-mode-placeholder phase-${view.phase}`}>
    <span>{String(slide?.index || view.currentSlide).padStart(2, "0")}</span>
    <h2>{slide?.title || view.headline}</h2>
    <p>{view.detailText}</p>
  </div>;
}

function SlideStateIcon({ status }: { status: ImageModeSlideStatus }) {
  if (status === "succeeded") return <i className="done"><IconCheck size={11}/></i>;
  if (status === "running") return <i className="running"><IconLoader2 size={12}/></i>;
  if (status === "failed") return <i className="failed"><IconAlertTriangle size={11}/></i>;
  return <i><IconClock size={11}/></i>;
}

function slideStateGlyph(status: ImageModeSlideStatus) {
  if (status === "succeeded") return <IconCheck size={12}/>;
  if (status === "running") return <IconLoader2 size={13}/>;
  if (status === "failed") return <IconAlertTriangle size={12}/>;
  return <IconClock size={12}/>;
}

function formatImageModeHeaderTime(value: string): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "--:--";
  return date.toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" });
}

function pickImageModeRender(detail?: ProjectDetailResponse): ProjectRender | undefined {
  if (!detail) return undefined;
  return [...detail.renders].reverse().find((render) =>
    render.renderer === "image2"
    && !EXCLUDED_IMAGE2_PRODUCT_CODES.has(render.productCode || "")
    && render.status !== "deleted",
  );
}

export function deriveImageModeCenterPhase(
  detail?: ProjectDetailResponse,
  imageRender?: ProjectRender,
  receivedFirstImage = false,
): ImageModeCenterPhase {
  if (receivedFirstImage || hasImageModeOutput(detail, imageRender)) return "images";
  if (detail?.task.workflowMode === "existing_ppt_beautify" && detail.task.errorCode === "insufficient_credits") return "parsing";
  if (detail?.task.workflowMode === "existing_ppt_beautify"
    && (["queued", "running"].includes(imageRender?.status || "")
      || ["ready_to_generate", "generating"].includes(detail.task.status))) return "images";
  if (detail?.planningPreview) return "outline";
  return "parsing";
}

export function existingPptAutoFollowSlideIndex(
  slides: Array<{ index: number; status: ImageModeSlideStatus; imageUrl?: string }>,
  view: Pick<ImageModeView, "phase" | "currentSlide">,
): number | undefined {
  if (!["queued", "rendering"].includes(view.phase) || !slides.length) return undefined;
  const runningIndex = slides.findIndex((slide) => slide.status === "running");
  if (runningIndex >= 0) return runningIndex;
  const reportedIndex = slides.findIndex((slide) => slide.index === view.currentSlide);
  return reportedIndex >= 0 ? reportedIndex : 0;
}

export function existingPptSlideCanOpen(slide?: { status?: ImageModeSlideStatus; imageUrl?: string }): boolean {
  return Boolean(slide?.imageUrl) || slide?.status === "running";
}

function findOpenableSlideIndex(
  slides: Array<{ status?: ImageModeSlideStatus; imageUrl?: string }>,
  currentIndex: number,
  direction: -1 | 1,
): number | undefined {
  for (let index = currentIndex + direction; index >= 0 && index < slides.length; index += direction) {
    if (existingPptSlideCanOpen(slides[index])) return index;
  }
  return undefined;
}

function hasImageModeOutput(detail?: ProjectDetailResponse, imageRender?: ProjectRender): boolean {
  if (detail?.imageModeProgress?.slides.some((slide) => Boolean(slide.imageUrl))) return true;
  return Boolean(imageRender?.outputManifest?.slides.some((slide) => Boolean(renderSlideImageUrl(imageRender, slide))));
}

function deriveImageModeView(detail?: ProjectDetailResponse, imageRender?: ProjectRender): ImageModeView {
  const existingPpt = detail?.task.workflowMode === "existing_ppt_beautify";
  const totalSlides = countTotalSlides(detail, imageRender);
  const completedSlides = completedImageSlides(detail, imageRender, totalSlides);
  const runningImageSlide = detail?.imageModeProgress?.slides.find((slide) => slide.status === "running");
  const failedImageSlide = detail?.imageModeProgress?.slides.find((slide) => slide.status === "failed");
  const runningSlide = detail?.slideProgress.find((slide) => slide.status === "running");
  const failedSlide = detail?.slideProgress.find((slide) => slide.status === "failed");
  const currentSlide = Math.min(totalSlides, Math.max(1,
    detail?.imageModeProgress?.currentSlide || runningImageSlide?.index || runningSlide?.slideIndex || failedImageSlide?.index || failedSlide?.slideIndex || completedSlides + 1,
  ));
  const taskStatus = detail?.task.status || "planning";
  const awaitingCredits = existingPpt && taskStatus === "ready_to_generate" && detail?.task.errorCode === "insufficient_credits";
  const preparedRetryNeedsRecovery = taskStatus === "ready_to_generate" && Number(detail?.task.retryCount || 0) > 0;
  const renderStatus = imageRender?.status || "";
  const hasImageOutput = Boolean(imageRender?.outputManifest?.slides.length);
  const phase: ImageModePhase = renderStatus === "succeeded" && hasImageOutput ? "complete"
    : renderStatus === "failed" || taskStatus === "failed" || preparedRetryNeedsRecovery ? "failed"
      : renderStatus === "succeeded" || taskStatus === "succeeded" ? "failed"
      : renderStatus === "cancelled" || taskStatus === "cancelled" ? "cancelled"
        : awaitingCredits ? "awaiting_credits"
        : renderStatus === "running" || taskStatus === "generating" || completedSlides > 0 ? "rendering"
          : renderStatus === "queued" || taskStatus === "ready_to_generate" ? "queued"
            : "planning";
  const taskProgress = Math.max(0, Math.min(100, Math.round(detail?.task.progressPercent || 0)));
  const slideProgress = totalSlides > 0 ? Math.round((completedSlides / totalSlides) * 95) : 0;
  const percent = phase === "complete" ? 100
    : phase === "failed" || phase === "cancelled" ? Math.max(taskProgress, slideProgress)
      : Math.max(taskProgress, slideProgress, phase === "planning" ? 12 : phase === "queued" ? 22 : 35);
  if (phase === "awaiting_credits") return {
    phase, percent: Math.max(58, percent), totalSlides, completedSlides, currentSlide,
    headline: "内容识别完成",
    subline: `已完成 ${totalSlides} 页解析，等待生成积分`,
    detailText: userFacingGenerationMessage(detail?.task.errorMessage, "生成积分不足。购买后可直接继续，不会重新解析 PPT。"),
  };
  if (phase === "complete") return {
    phase, percent: 100, totalSlides, completedSlides: totalSlides, currentSlide,
    headline: existingPpt ? "现有 PPT 美化完成" : "图片模式生成完成",
    subline: existingPpt ? `共 ${totalSlides} 页，原页序与内容边界均已保留` : `共 ${totalSlides} 页，已生成整页图片版 PPT`,
    detailText: existingPpt ? "美化结果已就绪，可逐页检查和下载。" : "图片模式结果已就绪，可逐页检查和下载。",
  };
  if (phase === "failed") return {
    phase, percent, totalSlides, completedSlides, currentSlide,
    headline: existingPpt ? "现有 PPT 美化失败" : "图片模式生成失败",
    subline: "可以从失败页面继续生成",
    detailText: userFacingGenerationMessage(failedImageSlide?.errorMessage || failedSlide?.errorMessage || imageRender?.errorMessage || detail?.task.errorMessage, "生成未完成，请稍后重试。"),
  };
  if (phase === "cancelled") return {
    phase, percent, totalSlides, completedSlides, currentSlide,
    headline: existingPpt ? "现有 PPT 美化已取消" : "图片模式生成已取消",
    subline: "项目内容仍会保留",
    detailText: existingPpt ? "原始 PPT 与已完成页面仍会保留，可以继续生成未完成页面。" : "已完成页面会保留，可以继续生成未完成页面。",
  };
  if (phase === "rendering") return {
    phase, percent: Math.min(99, percent), totalSlides, completedSlides, currentSlide,
    headline: existingPpt ? "正在美化现有 PPT" : "正在生成图片模式 PPT",
    subline: `${existingPpt ? "正在美化" : "正在生成"}第 ${currentSlide} 页 / 共 ${totalSlides} 页`,
    detailText: userFacingGenerationMessage(runningImageSlide?.progressText || runningSlide?.progressText || detail?.task.progressText, `已完成 ${completedSlides}/${totalSlides} 页，正在${existingPpt ? "保留原始内容并优化视觉" : "生成整页图片"}。单页通常需要 2–5 分钟，可以稍后回来查看。`),
  };
  if (phase === "queued") return {
    phase, percent: Math.min(99, percent), totalSlides, completedSlides, currentSlide,
    headline: existingPpt ? "PPT 美化任务已排队" : "图片模式任务已排队",
    subline: "正在等待渲染资源",
    detailText: userFacingGenerationMessage(detail?.task.progressText, existingPpt ? "原稿已安全保存，正在准备逐页美化任务。" : "项目已创建成功，正在准备图片生成任务。"),
  };
  return {
    phase, percent: Math.min(99, percent), totalSlides, completedSlides, currentSlide,
    headline: existingPpt ? "正在解析现有 PPT" : "正在规划图片模式 PPT",
    subline: existingPpt ? "正在读取原稿结构、页面内容与视觉素材" : "正在解析资料并生成页面结构",
    detailText: userFacingGenerationMessage(detail?.task.progressText, existingPpt ? "原文件不会被覆盖，系统正在准备逐页美化。" : "系统正在理解资料、规划页面内容和图片模式风格。"),
  };
}

export function buildImageModeSlides(detail?: ProjectDetailResponse, imageRender?: ProjectRender): ImageModeSlide[] {
  const outputSlides = imageRender?.outputManifest?.slides || [];
  const total = countTotalSlides(detail, imageRender);
  const outlineSlides = detail?.planningPreview?.snapshot.slides || [];
  const deckSlides = detail?.deckVersion?.document.slides || [];
  const recognitionSlides = detail?.existingPptRecognition?.slides || [];
  return Array.from({ length: total }, (_, offset) => {
    const index = offset + 1;
    const output = outputSlides.find((slide) => slide.index === index);
    const imageProgress = detail?.imageModeProgress?.slides.find((slide) => slide.index === index);
    const progress = detail?.slideProgress.find((slide) => slide.slideIndex === index);
    const outline = outlineSlides.find((slide) => slide.index === index);
    const deck = deckSlides.find((slide) => slide.index === index);
    const recognition = recognitionSlides.find((slide) => slide.index === index);
    const cacheVersion = imageRender?.updatedAt;
    const imageUrl = output ? renderSlideImageUrl(imageRender, output, false, cacheVersion) : imageProgress?.imageUrl;
    const thumbnailUrl = output ? renderSlideImageUrl(imageRender, output, true, cacheVersion) : imageProgress?.thumbnailUrl || imageProgress?.imageUrl || recognition?.previewUrl;
    return {
      sourceSlideId: output?.sourceSlideId,
      index,
      title: output?.title || outline?.title || deck?.title || recognition?.title || (index === 1 ? detail?.task.title || "封面" : `第 ${index} 页`),
      status: imageUrl ? "succeeded" : normalizeSlideStatus(imageProgress?.status || progress?.status),
      imageUrl,
      thumbnailUrl,
      sourcePreviewUrl: recognition?.previewUrl,
      progressText: imageProgress?.progressText || progress?.progressText,
      errorMessage: imageProgress?.errorMessage || progress?.errorMessage,
    };
  });
}

export function countTotalSlides(detail?: ProjectDetailResponse, imageRender?: ProjectRender): number {
  const requestedPageCount = detail?.task.requestedPageCount;
  const validRequestedPageCount = Number.isInteger(requestedPageCount) && Number(requestedPageCount) > 0
    ? Number(requestedPageCount)
    : 0;
  const observedPageCount = Math.max(
    imageRender?.outputManifest?.slides.length || 0,
    detail?.imageModeProgress?.totalSlides || 0,
    detail?.deckVersion?.document.slides.length || 0,
    detail?.planningPreview?.snapshot.pageCount || 0,
    detail?.planningPreview?.snapshot.slides?.length || 0,
    detail?.existingPptRecognition?.slideCount || 0,
    detail?.slideProgress.reduce((max, slide) => Math.max(max, slide.slideIndex), 0) || 0,
  );
  if (detail?.task.workflowMode === "existing_ppt_beautify") {
    return Math.max(1, observedPageCount || validRequestedPageCount);
  }
  return validRequestedPageCount || Math.max(1, observedPageCount);
}

function completedImageSlides(detail: ProjectDetailResponse | undefined, imageRender: ProjectRender | undefined, totalSlides: number): number {
  if (imageRender?.status === "succeeded" && imageRender.outputManifest?.slides.length) return totalSlides;
  if (detail?.imageModeProgress) return Math.min(totalSlides, detail.imageModeProgress.completedSlides);
  const completed = detail?.slideProgress.filter((slide) => slide.status === "succeeded" || slide.status === "complete").length || 0;
  return Math.min(totalSlides, completed);
}

function normalizeSlideStatus(status?: string): ImageModeSlideStatus {
  if (status === "succeeded" || status === "complete") return "succeeded";
  if (status === "running" || status === "generating") return "running";
  if (status === "failed") return "failed";
  if (status === "cancelled") return "cancelled";
  return "waiting";
}

function renderSlideImageUrl(render: ProjectRender | undefined, slide: OutputSlide | undefined, thumbnail = false, cacheVersion?: string): string | undefined {
  if (!render || !slide) return undefined;
  const resource = thumbnail ? slide.thumbnail?.url || slide.preview?.url : slide.preview?.url;
  if (!resource) return undefined;
  const signed = signedRenderResourceUrl(render, resource);
  if (signed) return appendImageVersion(signed, cacheVersion);
  const relativePath = /\/preview\/(.+)$/.exec(resource)?.[1];
  const signedBase = render.previewUrl?.replace(/index\.html(?:\?.*)?$/, "");
  if (relativePath && signedBase) return appendImageVersion(`${signedBase}${relativePath}`, cacheVersion);
  if (signedBase && !/^https?:\/\//i.test(resource) && !resource.startsWith("/")) return appendImageVersion(`${signedBase}${resource.replace(/^\/+/, "")}`, cacheVersion);
  return appendImageVersion(resource, cacheVersion);
}

function appendImageVersion(url: string, version?: string): string {
  if (!version) return url;
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}df_revision=${encodeURIComponent(version)}`;
}

function signedRenderResourceUrl(render: ProjectRender, url?: string): string | undefined {
  if (!url) return undefined;
  if (!render.previewUrl) return url;
  if (url.startsWith("/api/app/v2/render-previews/")) return url;
  const token = /\/api\/app\/v2\/render-previews\/([^/]+)\//.exec(render.previewUrl)?.[1];
  const prefix = `/api/app/v2/renders/${render.id}/preview/`;
  if (!token || !url.startsWith(prefix)) return undefined;
  return `/api/app/v2/render-previews/${token}/${url.slice(prefix.length)}`;
}

function badgeTone(phase: ImageModePhase): "brand" | "success" | "warning" | "danger" | "neutral" {
  if (phase === "complete") return "success";
  if (phase === "failed") return "danger";
  if (phase === "cancelled") return "warning";
  if (phase === "awaiting_credits") return "warning";
  if (phase === "planning" || phase === "queued" || phase === "rendering") return "brand";
  return "neutral";
}

function phaseLabel(phase: ImageModePhase): string {
  if (phase === "complete") return "已完成";
  if (phase === "failed") return "生成失败";
  if (phase === "cancelled") return "已取消";
  if (phase === "awaiting_credits") return "等待积分";
  if (phase === "rendering") return "生成中";
  if (phase === "queued") return "排队中";
  return "规划中";
}

export function shouldPollImageMode(
  detail: ProjectDetailResponse | undefined,
  imageRender: ProjectRender | undefined,
  initialLoadResolved: boolean,
): boolean {
  if (!initialLoadResolved || !detail) return false;
  return ["planning", "queued", "rendering"].includes(deriveImageModeView(detail, imageRender).phase);
}

export function imageModeResultToDetail(result: ProjectImageModeResultResponse): ProjectDetailResponse {
  if (!result.completed || !result.task || !result.currentRender) {
    throw new Error("图片模式结果尚未完成");
  }
  const currentRender = {
    ...result.currentRender,
    outputManifest: result.currentRender.outputManifest
      ? { ...result.currentRender.outputManifest, slides: result.pagePreviews || [] }
      : null,
  } as ProjectRender;
  return {
    ok: true,
    task: result.task,
    sourceFactsVersion: null,
    deckVersion: null,
    deckVersions: [],
    styleContractVersion: null,
    renders: [currentRender],
    revisionPreviews: [],
    generations: [],
    slideProgress: [],
    modelUsage: { callCount: 0, inputTokens: 0, outputTokens: 0, costFen: 0, latencyMs: 0 },
  };
}

function progressIcon(phase: ImageModePhase) {
  if (phase === "complete") return <IconCheck size={25}/>;
  if (phase === "failed") return <IconAlertTriangle size={25}/>;
  if (phase === "cancelled") return <IconClock size={25}/>;
  return <IconLoader2 size={25}/>;
}

function loadSessionDetail(taskId: string): ProjectDetailResponse | undefined {
  try {
    const value = JSON.parse(sessionStorage.getItem(`pptdeep:task:${taskId}`) || "null") as ProjectDetailResponse | undefined;
    return value?.ok ? value : undefined;
  } catch {
    return undefined;
  }
}
